import List from "@mui/material/List";
import { toast } from "react-toastify";
import Input from "@mui/material/Input";
import Avatar from "@mui/material/Avatar";
import * as api from "../../../shared/lib/api";
import ListItem from "@mui/material/ListItem";
import { ToastContainer } from "react-toastify";
import { useCore } from "../../../shared/hooks/use-core";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import FormControl from "@mui/material/FormControl";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import { Link, useNavigate } from "react-router-dom";
import ListItemText from "@mui/material/ListItemText";
import BusinessIcon from "@mui/icons-material/Business";
import { ListItemSecondaryAction } from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import InputAdornment from "@mui/material/InputAdornment";
import ModalDelete from "../../../shared/components/modal-delete";
import { Paginator } from "../../../shared/components/paginator";
import { useEffect, useLayoutEffect, useState } from "react";
import {
	CompanyProps,
	ListCompanyProps,
} from "../../../features/company/types/company";
import CircularProgress from "@mui/material/CircularProgress";
import { useDebounce } from "../../../shared/hooks/use-debounce";
import { normalizeCompanySearchTerm } from "../../../features/company/utils/normalize-company-search-term";

export default function Companies(): JSX.Element {
	const navigate = useNavigate();
	const { setTitleBar, setPathTitleBar } = useCore();

	const [companiesSearch, setCompaniesSearch] = useState<
		CompanyProps[] | undefined
	>([]);
	const [open, setOpen] = useState<boolean>(false);
	const [idDelete, setIdDelete] = useState<string | undefined>();
	const [searchValue, setSearchValue] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(true);
	const [count, setCount] = useState<number>(0);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const debouncedSearch = useDebounce(searchValue, 500);

	// monta a URL sempre mantendo minimal=true; usa ?search= quando houver termo (>=2)
	const buildListUrl = (page: number, rawTerm: string) => {
		const base = `company/list/?page=${page}&minimal=true`;
		const term = normalizeCompanySearchTerm(rawTerm).trim();
		return term.length >= 2
			? `${base}&search=${encodeURIComponent(term)}`
			: base;
	};

	// Paginação
	const handleChangePage = async (
		event: React.ChangeEvent<unknown>,
		value: number,
	) => {
		setLoading(true);
		setPageNumber(value);
		await getCompanies(buildListUrl(value, debouncedSearch));
	};

	// Busca empresas
	const getCompanies = async (url: string) => {
		const response = (await api.get(url)) as ListCompanyProps;
		setCount(response.content.count);
		setCompaniesSearch(response.content.results);
		setLoading(false);
	};

	// Input de busca (apenas controla estado; sem filtro local)
	const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
	};

	// Modal delete
	function handleModalOpen(id: string) {
		setIdDelete(id);
		setOpen(true);
	}

	const handleModalClose = () => setOpen(false);

	const handleDelete = async () => {
		setLoading(true);
		const response = (await api.destroy(`company/destroy/${idDelete}/`)) as {
			[key: string]: any;
		};
		if (response.status === 204) {
			toast.success("Empresa excluida com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});

			await getCompanies(buildListUrl(pageNumber, debouncedSearch));
		} else {
			toast.error("Falha ao tentar excluir empresa!", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
		handleModalClose();
		setLoading(false);
	};

	useEffect(() => {
		getCompanies("company/list/?minimal=true").catch(console.error);
	}, []);

	// Busca automática com debounce (reseta para a página 1)
	useEffect(() => {
		if (debouncedSearch.trim().length === 0) {
			setLoading(true);
			setPageNumber(1);
			getCompanies(buildListUrl(1, "")).catch(console.error);
		} else if (debouncedSearch.trim().length >= 2) {
			setLoading(true);
			setPageNumber(1);
			getCompanies(buildListUrl(1, debouncedSearch)).catch(console.error);
		}
	}, [debouncedSearch]);

	useLayoutEffect(() => {
		setTitleBar("Empresas");
		setPathTitleBar("/new-company");
	}, [setPathTitleBar, setTitleBar]);

	return (
		<>
			<ToastContainer />
			<ModalDelete
				open={open}
				textBody="Tem certeza que deseja excluir essa empresa? Todos os usuários relacionados a ela irão perder seus acessos."
				handleModalClose={handleModalClose}
				handleDelete={handleDelete}
				loading={loading}
			/>

			{/* Pesquisa */}
			<List dense disablePadding sx={{ width: "100%" }}>
				<ListItem disablePadding>
					<FormControl fullWidth sx={{ m: 1 }} variant="standard">
						<InputLabel htmlFor="input-search">Pesquisar</InputLabel>
						<Input
							id="input-search"
							startAdornment={
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							}
							value={searchValue}
							onChange={handleChangeSearch}
						/>
					</FormControl>
				</ListItem>

				{/* Lista de empresas ou spinner interno */}
				{loading ? (
					<ListItem disablePadding>
						<ListItemAvatar>
							<CircularProgress size={24} />
						</ListItemAvatar>
						<ListItemText primary="Carregando empresas…" />
					</ListItem>
				) : (
					companiesSearch?.map((item) => (
						<ListItem key={item.id} disablePadding>
							<Link to={`/company/${item.id}`} className="w-100">
								<ListItemButton>
									<ListItemAvatar>
										<Avatar>
											<BusinessIcon />
										</Avatar>
									</ListItemAvatar>
									<ListItemText
										primary={item.corporate_name}
										secondary={item.fantasy_name}
									/>
								</ListItemButton>
							</Link>
							<ListItemSecondaryAction>
								<IconButton
									sx={{ mr: 1 }}
									edge="end"
									aria-label="users"
									onClick={() =>
										navigate(`/users/${item.id}`, {
											state: { fantasy_name: item.fantasy_name },
										})
									}
								>
									<PeopleIcon />
								</IconButton>
								<IconButton
									edge="end"
									aria-label="delete"
									onClick={() => handleModalOpen(item.id)}
								>
									<DeleteIcon />
								</IconButton>
							</ListItemSecondaryAction>
						</ListItem>
					))
				)}
			</List>

			{/* Paginação sempre visível */}
			<Paginator
				count={count}
				pageNumber={pageNumber}
				onChange={handleChangePage}
			/>
		</>
	);
}
